import React, { useState } from 'react';
import { PlusCircle, Trash2, Edit2, Check, X, FileText, Save } from 'lucide-react';
import useCustomAnnouncements from '../hooks/useCustomAnnouncements';

const AnnouncementTemplateManager = ({ isDarkMode, currentRoutine, onAnnounce }) => {
  const {
    templates,
    selectedTemplate,
    setSelectedTemplate,
    addTemplate,
    editTemplate,
    deleteTemplate,
    applyTemplate
  } = useCustomAnnouncements();
  
  const [showNewForm, setShowNewForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [previewText, setPreviewText] = useState('');

  // Template variables that can be used
  const templateVariables = [
    { name: '{routineNumber}', description: 'The routine number' },
    { name: '{routineName}', description: 'The routine name' },
    { name: '{performerName}', description: 'Name of the performer (for solos)' },
    { name: '{studioCode}', description: 'Studio code/name' },
    { name: '{ageGroup}', description: 'Age group' },
    { name: '{style}', description: 'Dance style' },
    { name: '{category}', description: 'Category' },
    { name: '{division}', description: 'Division' },
    { name: '{level}', description: 'Level' },
  ];

  // Helper function to insert template variables
  const insertVariable = (variable) => {
    setTemplateContent(prev => prev + variable);
  };

  // Handle template selection
  const handleSelectTemplate = (id) => {
    setSelectedTemplate(id);
    
    // Update preview if we have a routine
    if (currentRoutine) {
      const template = templates.find(t => t.id === id);
      if (template) {
        setPreviewText(applyTemplate(currentRoutine));
      }
    }
  };

  // Handle template application
  const handleApplyTemplate = () => {
    if (!currentRoutine) return;
    
    const announcement = applyTemplate(currentRoutine);
    if (announcement && onAnnounce) {
      onAnnounce(announcement);
    }
  };

  // Handle form submissions
  const handleCreateTemplate = (e) => {
    e.preventDefault();
    if (!templateName.trim() || !templateContent.trim()) return;
    
    addTemplate(templateName.trim(), templateContent.trim());
    setShowNewForm(false);
    setTemplateName('');
    setTemplateContent('');
  };

  const handleSaveEdit = (e) => {
    e.preventDefault();
    if (!templateName.trim() || !templateContent.trim() || !editingId) return;
    
    editTemplate(editingId, templateName.trim(), templateContent.trim());
    setShowEditForm(false);
    setTemplateName('');
    setTemplateContent('');
    setEditingId(null);
  };

  const handleStartEdit = (template) => {
    setEditingId(template.id);
    setTemplateName(template.name);
    setTemplateContent(template.template);
    setShowEditForm(true);
    setShowNewForm(false);
  };

  const handleStartCreate = () => {
    setTemplateName('');
    setTemplateContent('');
    setShowNewForm(true);
    setShowEditForm(false);
  };

  const handleCancel = () => {
    setShowNewForm(false);
    setShowEditForm(false);
    setTemplateName('');
    setTemplateContent('');
    setEditingId(null);
  };

  return (
    <div className={`rounded-lg ${
      isDarkMode ? 'bg-gray-800' : 'bg-white'
    } shadow-lg border ${
      isDarkMode ? 'border-gray-700' : 'border-gray-200'
    } p-4`}>
      <h3 className="text-lg font-semibold mb-4">Announcement Templates</h3>
      
      {/* Template list section */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium">Choose a template:</div>
          <button
            onClick={handleStartCreate}
            className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition-all ${
              isDarkMode
                ? 'bg-blue-900/30 hover:bg-blue-800/40 text-blue-400'
                : 'bg-blue-100 hover:bg-blue-200 text-blue-600'
            }`}
          >
            <PlusCircle className="h-3 w-3" />
            New Template
          </button>
        </div>
        
        <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar">
          {templates.map(template => (
            <div 
              key={template.id}
              className={`flex items-center justify-between p-2 rounded-lg cursor-pointer transition-all ${
                selectedTemplate === template.id
                  ? isDarkMode
                    ? 'bg-blue-900/30 ring-1 ring-blue-500/30'
                    : 'bg-blue-100 ring-1 ring-blue-200'
                  : isDarkMode
                    ? 'bg-gray-700 hover:bg-gray-600'
                    : 'bg-gray-100 hover:bg-gray-200'
              }`}
              onClick={() => handleSelectTemplate(template.id)}
            >
              <div className="flex items-center gap-2">
                <FileText className="h-4 w-4 text-gray-400" />
                <span className="text-sm font-medium">{template.name}</span>
              </div>
              
              <div className="flex items-center gap-1">
                {template.isCustom && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEdit(template);
                      }}
                      className={`p-1 rounded-lg transition-all ${
                        isDarkMode
                          ? 'hover:bg-gray-600'
                          : 'hover:bg-gray-300'
                      }`}
                    >
                      <Edit2 className="h-3 w-3 text-gray-400" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteTemplate(template.id);
                      }}
                      className={`p-1 rounded-lg transition-all ${
                        isDarkMode
                          ? 'hover:bg-gray-600'
                          : 'hover:bg-gray-300'
                      }`}
                    >
                      <Trash2 className="h-3 w-3 text-gray-400" />
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Form for creating or editing templates */}
      {(showNewForm || showEditForm) && (
        <form onSubmit={showEditForm ? handleSaveEdit : handleCreateTemplate} className="mb-4">
          <div className="space-y-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Template Name</label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg ${
                  isDarkMode
                    ? 'bg-gray-700 text-white border-gray-600'
                    : 'bg-white text-gray-800 border-gray-300'
                } border`}
                placeholder="e.g., My Custom Intro"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Template Content</label>
              <textarea
                value={templateContent}
                onChange={(e) => setTemplateContent(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg resize-none ${
                  isDarkMode
                    ? 'bg-gray-700 text-white border-gray-600'
                    : 'bg-white text-gray-800 border-gray-300'
                } border`}
                rows={4}
                placeholder="e.g., Welcome routine {routineNumber}! A {ageGroup} {category}..."
                required
              />
              
              <div className="mt-2">
                <label className="block text-xs font-medium mb-1">Insert Variable:</label>
                <div className="flex flex-wrap gap-1">
                  {templateVariables.map(variable => (
                    <button
                      key={variable.name}
                      type="button"
                      onClick={() => insertVariable(variable.name)}
                      className={`px-2 py-1 text-xs rounded-lg transition-all ${
                        isDarkMode
                          ? 'bg-gray-700 hover:bg-gray-600'
                          : 'bg-gray-100 hover:bg-gray-200'
                      }`}
                      title={variable.description}
                    >
                      {variable.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={handleCancel}
              className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600'
                  : 'bg-gray-200 hover:bg-gray-300'
              }`}
            >
              <X className="h-4 w-4 inline mr-1" />
              Cancel
            </button>
            
            <button
              type="submit"
              className={`px-3 py-1.5 text-sm rounded-lg transition-all ${
                isDarkMode
                  ? 'bg-blue-600 hover:bg-blue-700'
                  : 'bg-blue-500 hover:bg-blue-600'
              } text-white`}
            >
              <Save className="h-4 w-4 inline mr-1" />
              {showEditForm ? 'Save Changes' : 'Create Template'}
            </button>
          </div>
        </form>
      )}
      
      {/* Preview and application section */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-medium">Preview:</div>
          <button
            onClick={handleApplyTemplate}
            disabled={!currentRoutine}
            className={`px-3 py-1.5 text-xs rounded-lg transition-all ${
              isDarkMode
                ? 'bg-blue-600 hover:bg-blue-700'
                : 'bg-blue-500 hover:bg-blue-600'
            } text-white ${!currentRoutine ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <Check className="h-3 w-3 inline mr-1" />
            Announce
          </button>
        </div>
        
        <div
          className={`p-3 rounded-lg border ${
            isDarkMode
              ? 'bg-gray-700/50 border-gray-600'
              : 'bg-gray-50 border-gray-200'
          } min-h-16 text-sm`}
        >
          {currentRoutine && selectedTemplate
            ? previewText || applyTemplate(currentRoutine)
            : 'Select a template and routine to see preview'}
        </div>
      </div>
    </div>
  );
};

export default AnnouncementTemplateManager; 